import React, { useState, useEffect } from 'react';
import { 
    Filter, Play, Loader2, Download, Search, AlertCircle, FileBarChart,
    ChevronLeft, ArrowLeft
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { format, subDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function RunCustomReportPage({ reportId, handleNavigation }) {
    const [reportMeta, setReportMeta] = useState(null);
    const [loadingMeta, setLoadingMeta] = useState(true);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    // Filters
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(1); // Ayın 1'i
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

    useEffect(() => {
        const fetchMeta = async () => {
            try {
                const res = await apiClient(`reports/custom/${reportId}`);
                setReportMeta(res);
            } catch (e) {
                setError("Rapor bilgileri yüklenemedi: " + e.message);
            } finally {
                setLoadingMeta(false);
            }
        };
        fetchMeta();
    }, [reportId]);

    const handleRun = async () => {
        setRunning(true);
        setError(null);
        setResult(null);
        try {
            const payload = {
                startDate: new Date(startDate).toISOString(),
                endDate: new Date(endDate).toISOString(),
            };
            const res = await apiClient(`reports/custom/${reportId}/run`, {
                method: 'POST',
                body: payload
            });
            setResult(res);
        } catch (e) {
            setError("Rapor çalıştırılırken hata oluştu: " + e.message);
        } finally {
            setRunning(false);
        }
    };

    const exportToExcel = () => {
        if (!result) return;
        const worksheet = XLSX.utils.aoa_to_sheet([result.headers, ...result.rows]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Rapor");
        XLSX.writeFile(workbook, `${reportMeta?.name || 'Rapor'}.xlsx`);
    };

    const exportToPdf = async () => {
        if (!result) return;
        try {
            const { blob, filename } = await apiClient(`reports/custom/${reportId}/pdf`, {
                method: 'POST',
                body: {
                    startDate: new Date(startDate).toISOString(),
                    endDate: new Date(endDate).toISOString(),
                },
                responseType: 'blob'
            });
            saveAs(blob, filename);
        } catch (err) {
            setError("PDF indirilirken hata oluştu: " + err.message);
        }
    };

    if (loadingMeta) {
        return (
            <div className="flex-1 flex items-center justify-center min-h-[calc(100vh-64px)]">
                <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
            </div>
        );
    }

    if (error && !reportMeta) {
        return (
            <div className="p-6">
                <button onClick={() => handleNavigation('custom_reports')} className="flex items-center text-gray-500 hover:text-gray-700 mb-4">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Geri Dön
                </button>
                <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" /> {error}
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 w-full bg-gray-50 dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden min-h-[calc(100vh-64px)] flex flex-col">
            
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => handleNavigation('custom_reports')}
                        className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                        title="Listeye Dön"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                            <FileBarChart className="w-6 h-6 text-blue-500" />
                            {reportMeta?.name}
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Kaynak: {reportMeta?.baseReportType}
                        </p>
                    </div>
                </div>
            </div>

            {/* Content Area - Split Layout */}
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                
                {/* Filters Sidebar */}
                <div className="w-full md:w-80 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 flex flex-col gap-6 overflow-y-auto shrink-0">
                    <div>
                        <h3 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-4">
                            <Filter className="w-4 h-4" />
                            Rapor Filtreleri
                        </h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Başlangıç Tarihi</label>
                                <input 
                                    type="date" 
                                    value={startDate}
                                    onChange={e => setStartDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bitiş Tarihi</label>
                                <input 
                                    type="date" 
                                    value={endDate}
                                    onChange={e => setEndDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>
                    </div>

                    <button 
                        onClick={handleRun}
                        disabled={running}
                        className="w-full flex justify-center items-center px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold shadow-md hover:bg-blue-700 transition active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed mt-auto"
                    >
                        {running ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5 mr-2" />}
                        {running ? 'Çalıştırılıyor...' : 'Raporu Çalıştır'}
                    </button>
                </div>

                {/* Results Area */}
                <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-6 overflow-hidden flex flex-col">
                    {error && (
                        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-xl flex items-center">
                            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    {!result && !running && !error && (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl">
                            <Search className="w-16 h-16 mb-4 opacity-50" />
                            <p className="text-lg">Sonuçları görmek için "Raporu Çalıştır" butonuna tıklayın.</p>
                        </div>
                    )}

                    {running && (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                            <Loader2 className="w-12 h-12 animate-spin text-blue-500 mb-4" />
                            <p>Veriler hazırlanıyor, lütfen bekleyin...</p>
                        </div>
                    )}

                    {result && !running && (
                        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                                <span className="font-medium text-gray-700 dark:text-gray-200">
                                    Sonuçlar ({result.rows.length} satır)
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={exportToExcel}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition"
                                    >
                                        <Download className="w-4 h-4" /> Excel
                                    </button>
                                    <button
                                        onClick={exportToPdf}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition"
                                    >
                                        <Download className="w-4 h-4" /> PDF
                                    </button>
                                </div>
                            </div>
                            
                            <div className="flex-1 overflow-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-10 shadow-sm">
                                        <tr>
                                            {result.headers.map((h, i) => (
                                                <th key={i} className="px-6 py-4 font-bold whitespace-nowrap">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {result.rows.length === 0 ? (
                                            <tr>
                                                <td colSpan={result.headers.length} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                                                    Bu kriterlere uygun veri bulunamadı.
                                                </td>
                                            </tr>
                                        ) : (
                                            result.rows.map((row, rIndex) => (
                                                <tr key={rIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors bg-white dark:bg-gray-800">
                                                    {row.map((cell, cIndex) => (
                                                        <td key={cIndex} className="px-6 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                                            {cell}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
